"use client"

import {
  ArrowRight,
  BadgeCheck,
  ChevronDown,
  Envelope,
  MagnifyingGlass,
  MapPin,
  Phone,
  ShieldCheck,
} from "@medusajs/icons"
import LocalizedClientLink from "@modules/common/components/localized-client-link"
import { type ReactNode, useMemo, useState } from "react"

type FaqItem = {
  question: string
  category: string
  answer: ReactNode
}

const categories = ["All", "Pre Sale Questions"]

const faqs: FaqItem[] = [
  {
    question: "Will I Receive the Same Product that I See in the Picture?",
    category: "Pre Sale Questions",
    answer: (
      <div className="space-y-6">
        <p>
          <strong className="font-semibold text-[#1A1A1A]">
            Yes—99%+ Match Guaranteed.
          </strong>{" "}
          We photograph genuine, in-stock products and commit to delivering
          items that match our images with exceptional accuracy. Minor
          variations are unavoidable and imperceptible.
        </p>
        <div className="border-l-2 border-[#3D5240] bg-[#FFFFFF] p-4">
          <p className="mb-3 text-[12px] font-bold uppercase tracking-[0.14em] text-[#1A1A1A]">
            Guaranteed Items
          </p>
          <ul className="space-y-2 text-sm text-[#444748]">
            <li className="flex gap-2">
              <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3D5240]" />
              <span>Same design and pattern</span>
            </li>
            <li className="flex gap-2">
              <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3D5240]" />
              <span>Same color range</span>
            </li>
            <li className="flex gap-2">
              <BadgeCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#3D5240]" />
              <span>Same quality and material</span>
            </li>
          </ul>
        </div>
      </div>
    ),
  },
  {
    question: "Why the Slight Variation?",
    category: "Pre Sale Questions",
    answer: (
      <div className="space-y-6">
        <p>We&apos;re transparent about the ±1% difference:</p>
        <div className="grid gap-8 sm:grid-cols-2">
          <div>
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
              Device Display Variations (±0.5-1%)
            </h3>
            <ul className="space-y-2 text-sm leading-6">
              <li>
                Your phone or monitor displays colors differently based on
                brightness settings and screen technology.
              </li>
              <li>Studio lighting differs from natural home lighting.</li>
              <li>
                This accounts for imperceptible color tone shifts only—not
                actual product difference.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
              Human Factors in Photography (±0.5%)
            </h3>
            <ul className="space-y-2 text-sm leading-6">
              <li>
                Camera angle and lens type can create minimal size perception
                differences (no actual size change).
              </li>
              <li>
                Professional team review catches errors, keeping this below 1%.
              </li>
            </ul>
          </div>
          <div className="sm:col-span-2">
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
              Natural Product Variation (±0-1%)
            </h3>
            <ul className="space-y-2 text-sm leading-6">
              <li>
                Fabric texture and print alignment vary slightly per garment
                industry standards.
              </li>
              <li>
                These negligible differences don&apos;t affect quality or
                wearability.
              </li>
              <li>Acceptable across all fashion retailers globally.</li>
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
              What You&apos;re Guaranteed to Receive
            </h3>
            <ul className="space-y-2 text-sm leading-6">
              <li>
                <strong className="font-semibold text-[#1A1A1A]">
                  Same design and pattern
                </strong>{" "}
                — Exactly as shown.
              </li>
              <li>
                <strong className="font-semibold text-[#1A1A1A]">
                  Same color range
                </strong>{" "}
                — Within natural display variation.
              </li>
              <li>
                <strong className="font-semibold text-[#1A1A1A]">
                  Same size and dimensions
                </strong>{" "}
                — Per specifications.
              </li>
              <li>
                <strong className="font-semibold text-[#1A1A1A]">
                  Same quality and material
                </strong>{" "}
                — As described.
              </li>
            </ul>
          </div>
          <div>
            <h3 className="mb-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
              Our Multi-Stage Quality Process
            </h3>
            <ol className="space-y-2 text-sm leading-6">
              <li>1. Production check at manufacturing.</li>
              <li>2. Photography verification before images go live.</li>
              <li>3. Image accuracy cross-check with actual products.</li>
              <li>4. Pre-shipment inspection before dispatch.</li>
            </ol>
          </div>
        </div>
        <p>
          <strong className="font-semibold text-[#1A1A1A]">
            99%+ Accuracy Rate
          </strong>{" "}
          = Your product will match the photograph. Period.
        </p>
      </div>
    ),
  },
  {
    question: "If There's a Discrepancy?",
    category: "Pre Sale Questions",
    answer: (
      <div className="space-y-6">
        <p>
          While extremely rare, contact us with photos and check while receiving
          delivery:
        </p>
        <ul className="space-y-2 text-sm leading-6">
          <li>Immediate replacement or full refund.</li>
          <li>No questions asked.</li>
          <li>We cover return shipping.</li>
        </ul>
        <p>
          <strong className="font-semibold text-[#1A1A1A]">
            Your trust is our priority. We stand behind every product with 99%+
            confidence.
          </strong>
        </p>
      </div>
    ),
  },
]

export default function FaqPageTemplate() {
  const [activeCategory, setActiveCategory] = useState(categories[0])
  const [openQuestion, setOpenQuestion] = useState(faqs[0].question)
  const [query, setQuery] = useState("")

  const visibleFaqs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase()

    return faqs.filter((faq) => {
      const matchesCategory =
        activeCategory === "All" || faq.category === activeCategory
      const matchesQuery =
        !normalizedQuery ||
        faq.question.toLowerCase().includes(normalizedQuery) ||
        faq.category.toLowerCase().includes(normalizedQuery)

      return matchesCategory && matchesQuery
    })
  }, [activeCategory, query])

  return (
    <main className="bg-[#FFFFFF] text-[#1A1A1A]">
      <section className="relative overflow-hidden bg-[#FFFFFF] px-4 py-16 md:px-[4vw] md:py-24">
        <div className="relative z-10 mx-auto max-w-3xl text-center">
          <p className="text-[12px] font-bold uppercase tracking-[0.2em] text-[#3D5240]">
            Customer Care
          </p>
          <h1 className="mt-4 font-serif text-[40px] font-normal leading-[48px] text-[#1A1A1A] md:text-[64px] md:leading-[72px]">
            Frequently Asked Questions
          </h1>
          <label className="mx-auto mt-8 flex max-w-xl items-center gap-3 border-b border-[#1A1A1A]/10 bg-[#FFFFFF] px-4 py-4 text-left transition focus-within:border-[#3D5240] focus-within:[transform:scale(1.02)]">
            <MagnifyingGlass className="h-5 w-5 shrink-0 text-[#444748]" />
            <span className="sr-only">Search for answers</span>
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="min-w-0 flex-1 border-0 bg-transparent p-0 text-[16px] leading-6 text-[#1A1A1A] outline-none ring-0 placeholder:text-[#5f5e5e] focus:ring-0"
              placeholder="Search for answers..."
              type="search"
            />
          </label>
        </div>
        <div
          className="pointer-events-none absolute right-0 top-0 hidden h-full w-1/3 opacity-[0.04] md:block"
          style={{
            background:
              "repeating-linear-gradient(90deg, #1A1A1A, #1A1A1A 1px, transparent 1px, transparent 40px)",
          }}
        />
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-16 md:px-[4vw] md:py-20">
        <div className="grid grid-cols-1 items-start gap-12 lg:grid-cols-[minmax(240px,30%)_minmax(0,70%)] lg:gap-16">
          <aside className="space-y-8 lg:sticky lg:top-32">
            <div>
              <h2 className="border-b border-[#1A1A1A]/10 pb-4 text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
                Categories
              </h2>
              <div className="mt-4 flex gap-3 overflow-x-auto lg:flex-col lg:gap-4 lg:overflow-visible">
                {categories.map((category) => {
                  const active = category === activeCategory

                  return (
                    <button
                      key={category}
                      type="button"
                      onClick={() => {
                        setActiveCategory(category)
                        setOpenQuestion(faqs[0].question)
                      }}
                      className={`whitespace-nowrap border-l-2 py-1 pl-4 text-left text-[16px] leading-6 transition-colors ${
                        active
                          ? "border-[#3D5240] text-[#1A1A1A]"
                          : "border-transparent text-[#444748] hover:text-[#3D5240]"
                      }`}
                    >
                      {category}
                    </button>
                  )
                })}
              </div>
            </div>

            <div className="border border-[#1A1A1A]/10 bg-white p-8">
              <h2 className="text-[20px] font-semibold leading-7 tracking-[0.02em] text-[#1A1A1A]">
                Need Help?
              </h2>
              <p className="mt-4 text-sm leading-6 text-[#444748]">
                Our artisans and experts are here to assist with your journey.
              </p>
              <LocalizedClientLink
                href="/contact"
                className="mt-6 inline-flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.1em] text-[#3D5240] transition-transform hover:translate-x-1"
              >
                Message Us
                <ArrowRight className="h-4 w-4" />
              </LocalizedClientLink>
            </div>
          </aside>

          <div className="min-w-0 space-y-12">
            <section>
              <h2 className="mb-8 font-serif text-[32px] font-normal leading-10 text-[#1A1A1A]">
                {activeCategory === "All"
                  ? "Pre Sale Questions"
                  : activeCategory}
              </h2>
              <div className="bg-white">
                {visibleFaqs.map((faq) => {
                  const open = openQuestion === faq.question

                  return (
                    <article
                      key={faq.question}
                      className="border-b border-[#1A1A1A]/10"
                    >
                      <button
                        type="button"
                        aria-expanded={open}
                        onClick={() =>
                          setOpenQuestion(open ? "" : faq.question)
                        }
                        className="group flex w-full items-center justify-between gap-6 py-6 text-left"
                      >
                        <span className="text-[20px] font-semibold leading-7 tracking-[0.02em] text-[#1A1A1A] transition-colors group-hover:text-[#3D5240]">
                          {faq.question}
                        </span>
                        <ChevronDown
                          className={`h-5 w-5 shrink-0 text-[#1A1A1A] transition-transform duration-300 ${
                            open ? "rotate-180" : ""
                          }`}
                        />
                      </button>
                      <div
                        className={`grid transition-[grid-template-rows,opacity] duration-300 ${
                          open
                            ? "grid-rows-[1fr] opacity-100"
                            : "grid-rows-[0fr] opacity-0"
                        }`}
                      >
                        <div className="overflow-hidden">
                          <div className="pb-8 text-[16px] leading-7 text-[#444748]">
                            {faq.answer}
                          </div>
                        </div>
                      </div>
                    </article>
                  )
                })}
                {!visibleFaqs.length && (
                  <p className="border-b border-[#1A1A1A]/10 py-8 text-[16px] leading-7 text-[#444748]">
                    No questions match your search.
                  </p>
                )}
              </div>
            </section>

            <section className="grid grid-cols-1 gap-6 pt-2 md:grid-cols-3">
              <div className="flex flex-col justify-center bg-[#F5F5F5] p-10 text-[#1A1A1A] md:col-span-2">
                <h2 className="font-serif text-[32px] font-normal leading-10">
                  99%+ Accuracy Rate
                </h2>
                <p className="mt-4 max-w-md text-[16px] leading-6 text-[#444748]">
                  Your product will match the photograph. Period. Our
                  multi-stage process ensures every garment meets the heritage
                  standard.
                </p>
              </div>
              <div className="flex aspect-square flex-col justify-between bg-[#3D5240] p-8 text-[#FFFFFF]">
                <ShieldCheck className="h-10 w-10" />
                <p className="text-[12px] font-bold uppercase leading-6 tracking-[0.1em]">
                  Our Trust is our Priority. We stand behind every product.
                </p>
              </div>
            </section>
          </div>
        </div>
      </section>

      <section className="bg-[#e4e2de] px-4 py-20 text-center md:px-[4vw] md:py-24">
        <div className="mx-auto max-w-[1440px]">
          <h2 className="font-serif text-[32px] font-normal leading-10 text-[#1A1A1A]">
            Still have questions?
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-[16px] leading-6 text-[#444748]">
            Our dedicated concierge team is available to help with any inquiries
            regarding our collections or services.
          </p>

          <div className="mx-auto mt-12 grid max-w-4xl grid-cols-1 gap-8 md:grid-cols-3">
            <ContactCard
              icon={<Phone className="h-5 w-5" />}
              label="Telephone"
              value="09638 22 44 55"
              href="tel:09638224455"
            />
            <ContactCard
              icon={<Envelope className="h-5 w-5" />}
              label="Email"
              value={process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL || ""}
              href={`mailto:${process.env.NEXT_PUBLIC_BRAND_SUPPORT_EMAIL || ""}`}
            />
            <ContactCard
              icon={<MapPin className="h-5 w-5" />}
              label="Location"
              value={
                <>
                  Siaam Tower, L-12, Sector-3,
                  <br />
                  Uttara, Dhaka, Bangladesh
                </>
              }
            />
          </div>

          <LocalizedClientLink
            href="/contact"
            className="mt-16 inline-flex bg-[#1A1A1A] px-12 py-5 text-[12px] font-bold uppercase tracking-[0.2em] text-[#FFFFFF] transition-colors hover:bg-[#3D5240] hover:text-[#FFFFFF]"
          >
            Contact Support
          </LocalizedClientLink>
        </div>
      </section>
    </main>
  )
}

function ContactCard({
  icon,
  label,
  value,
  href,
}: {
  icon: ReactNode
  label: string
  value: ReactNode
  href?: string
}) {
  const content = (
    <>
      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#FFFFFF] text-[#3D5240]">
        {icon}
      </span>
      <span className="block text-[12px] font-bold uppercase tracking-[0.1em] text-[#1A1A1A]">
        {label}
      </span>
      <span className="block text-[15px] font-semibold leading-6 text-[#1A1A1A]">
        {value}
      </span>
    </>
  )

  if (href) {
    return (
      <a
        href={href}
        className="space-y-4 transition-colors hover:text-[#3D5240]"
      >
        {content}
      </a>
    )
  }

  return <div className="space-y-4">{content}</div>
}
